import { useEffect } from 'react';
import { FLAVORS, type Flavor } from '@web/shared';
import { useProfile } from './api';

/**
 * The signed-in user's profile "flavor" (Citron / Peach / Berry / …) drives an
 * app-wide accent theme: the `--u-flavor*` CSS custom properties, the ambient
 * background wash (`.app-aura`), and a few tinted surfaces (`bg-u`, `text-u-ink`,
 * `border-u`). This module swaps those vars at runtime; `public/theme-init.js`
 * applies the persisted value before first paint to avoid a flash.
 */

const KEY = 'nm.flavor';
const isFlavor = (v: unknown): v is Flavor =>
  typeof v === 'string' && (FLAVORS as readonly string[]).includes(v);

/** The flavor persisted from a previous session, if any. */
export function readStoredFlavorTheme(): Flavor | null {
  try {
    const v = localStorage.getItem(KEY);
    return isFlavor(v) ? v : null;
  } catch {
    return null;
  }
}

/** Point the `--u-flavor*` vars at a flavor. Safe to call on every keystroke. */
export function applyFlavorTheme(flavor: Flavor, persist = true): void {
  if (typeof document === 'undefined' || !isFlavor(flavor)) return;
  const s = document.documentElement.style;
  s.setProperty('--u-flavor', `var(--color-flavor-${flavor})`);
  s.setProperty('--u-flavor-soft', `var(--color-flavor-${flavor}-soft)`);
  s.setProperty('--u-flavor-ink', `var(--color-flavor-${flavor}-ink)`);
  if (persist) {
    try {
      localStorage.setItem(KEY, flavor);
    } catch {
      /* private mode — the picker preview still works for the session */
    }
  }
}

/** Keep the app's flavor theme in step with the signed-in profile. */
export function useSyncFlavorTheme(): void {
  const flavor = useProfile().data?.profile.flavor;
  useEffect(() => {
    if (isFlavor(flavor)) applyFlavorTheme(flavor);
  }, [flavor]);
}
